import React, { useEffect, useState } from "react";

import { Trash2 } from "lucide-react";
import Swal from "sweetalert2";

import SalesConditionSelector from "~/components/SalesConditionSelector";
import { Button } from "~/components/ui/button";
import { Checkbox } from "~/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Textarea } from "~/components/ui/textarea";
import { toast } from "~/hooks/use-toast";
import { useAuxiliaryTypes } from "~/hooks/useAuxiliaryTypes";
import { useSalesChannels } from "~/hooks/useSalesChannels";
import { useSalesConditions } from "~/hooks/useSalesConditions";
import { supabase } from "~/integrations/supabase/client";
import {
  findBSaleClientByRUT,
  syncClientToBSale,
} from "~/services/bsaleClientService";
import { useProfileStore } from "~/store/profileStore";
import { Client } from "~/types/client";
import { isValidEmail } from "~/utils/helpers";

interface EditClientModalProps {
  client: Client;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onClientUpdated: () => void;
}

// Define the client update data interface
interface ClientUpdateData {
  name: string;
  email: string;
  phone_number: string;
  contact_person?: string;
  activity?: string;
  sales_condition_id?: string | null;
  fiscal_address_id?: string | null;
  insured_amount?: number;
  trailing_payment_days?: number;
  risk_checked?: boolean;
  internal_notes?: string;
  collection_contact_person?: string;
  collection_contact_email?: string;
  collection_contact_phone?: string;
  sales_channel?: string;
  auxiliary_type?: string;
  zone?: string;
  siniestro?: boolean;
  cobranza_judicial?: boolean;
}

// Special sales condition ID that requires zeroing financial fields
const ZERO_FINANCIAL_CONDITION_ID = "c7b68ad1-3869-4f0b-b35b-e07a263fd43e";

const EditClientModal: React.FC<EditClientModalProps> = ({
  client,
  isOpen,
  onOpenChange,
  onClientUpdated,
}) => {
  const { role } = useProfileStore();
  const isAdmin = role === "superadmin" || role === "admin";
  const isSuperAdmin = role === "superadmin";
  const canManageRisk = isAdmin || role === "risk";

  const [formData, setFormData] = useState({
    name: client.name || "",
    email: client.email || "",
    phone: client.phone || "",
    contactPerson: client.contact_person || "",
    activity: client.activity || "",
    salesConditionId: client.salesConditionId || "",
    insuredAmount: client.insuredAmount || 0,
    trailingPaymentDays: client.trailingPaymentDays || 30,
    riskChecked: client.risk_checked || false,
    internalNotes: client.internal_notes || "",
    collectionContactPerson: (client as any).collection_contact_person || "",
    collectionContactEmail: (client as any).collection_contact_email || "",
    collectionContactPhone: (client as any).collection_contact_phone || "",
    salesChannel: (client as any).sales_channel || "",
    auxiliaryType: (client as any).auxiliary_type || "",
    zone: (client as any).zone || "",
    siniestro: client.siniestro || false,
    cobranzaJudicial: client.cobranza_judicial || false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [collectionEmailError, setCollectionEmailError] = useState<
    string | null
  >(null);
  const { loading: isLoadingSalesConditions, getConditionById } =
    useSalesConditions();

  // Fetch sales channels and auxiliary types
  const { salesChannels, isLoading: isLoadingSalesChannels } =
    useSalesChannels();
  const { auxiliaryTypes, isLoading: isLoadingAuxiliaryTypes } =
    useAuxiliaryTypes();

  // Determine if the current sales condition is prepaid type
  const isPrepaidCondition = () => {
    if (!formData.salesConditionId) return false;
    const condition = getConditionById(formData.salesConditionId);
    return condition?.type === "prepaid";
  };

  // Determine if the current sales condition is the special condition that requires zeroing financial fields
  const isSpecialZeroCondition = () => {
    return formData.salesConditionId === ZERO_FINANCIAL_CONDITION_ID;
  };

  // Update form data when client changes or modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        name: client.name || "",
        email: client.email || "",
        phone: client.phone || "",
        contactPerson: client.contact_person || "",
        activity: client.activity || "",
        salesConditionId: client.salesConditionId || "",
        insuredAmount: client.insuredAmount || 0,
        trailingPaymentDays: client.trailingPaymentDays || 30,
        riskChecked: client.risk_checked || false,
        internalNotes: client.internal_notes || "",
        collectionContactPerson:
          (client as any).collection_contact_person || "",
        collectionContactEmail: (client as any).collection_contact_email || "",
        collectionContactPhone: (client as any).collection_contact_phone || "",
        salesChannel: (client as any).sales_channel || "",
        auxiliaryType: (client as any).auxiliary_type || "",
        zone: (client as any).zone || "",
        siniestro: client.siniestro || false,
        cobranzaJudicial: client.cobranza_judicial || false,
      });
    }
  }, [client, isOpen]);

  // Reset financial fields when sales condition type changes to prepaid or special condition
  // Note: trailing_payment_days is now automatically managed by database trigger
  useEffect(() => {
    if (isPrepaidCondition() || isSpecialZeroCondition()) {
      setFormData((prev) => ({
        ...prev,
        insuredAmount: 0,
        trailingPaymentDays: 0,
      }));
    } else {
      // For non-prepaid conditions, set trailing_payment_days to match sales condition
      const condition = getConditionById(formData.salesConditionId);
      if (condition?.maximumDays !== undefined) {
        setFormData((prev) => ({
          ...prev,
          trailingPaymentDays: condition.maximumDays,
        }));
      }
    }
  }, [formData.salesConditionId, getConditionById]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === "email") setEmailError(null);
    if (name === "collectionContactEmail") setCollectionEmailError(null);
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: Number(value) || 0 }));
  };

  const handleSalesConditionChange = (value: string) => {
    setFormData((prev) => ({ ...prev, salesConditionId: value }));

    // Note: trailing_payment_days will be automatically updated by database trigger
    // when the client record is saved. We update the form state for immediate UI feedback.
    const condition = getConditionById(value);
    if (
      condition?.type === "prepaid" ||
      value === ZERO_FINANCIAL_CONDITION_ID
    ) {
      setFormData((prev) => ({
        ...prev,
        salesConditionId: value,
        insuredAmount: 0,
        trailingPaymentDays: 0,
      }));
    } else if (condition?.maximumDays !== undefined) {
      setFormData((prev) => ({
        ...prev,
        salesConditionId: value,
        trailingPaymentDays: condition.maximumDays,
      }));
    }
  };

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleRiskCheckedChange = (checked: boolean) => {
    setFormData((prev) => ({ ...prev, riskChecked: checked }));
  };

  const handleSiniestroChange = (checked: boolean) => {
    setFormData((prev) => ({ ...prev, siniestro: checked }));
  };

  const handleCobranzaJudicialChange = (checked: boolean) => {
    setFormData((prev) => ({ ...prev, cobranzaJudicial: checked }));
  };

  const handleDeleteClient = () => {
    Swal.fire({
      title: "¿Está seguro?",
      text: "Esta acción no se puede deshacer",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    }).then(async (result) => {
      if (result.isConfirmed) {
        const { error } = await supabase
          .from("clients")
          .update({ deleted_at: new Date().toISOString() })
          .eq("id", client.id);

        if (error) {
          toast({
            title: "Error",
            description: "No se pudo eliminar el cliente",
            variant: "destructive",
          });
        } else {
          toast({
            title: "Cliente eliminado",
            description: "El cliente ha sido eliminado correctamente",
          });
          onOpenChange(false);
          onClientUpdated();
        }
      }
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Email format validation. Both fields are optional (the historical "NAN"
    // cleanup nulled thousands of legacy emails, so we must not force a value on
    // edit), but a non-empty value must be a real address — otherwise BSale
    // rejects the DTE at factura time ("invalid client email").
    const email = formData.email.trim();
    const collectionEmail = formData.collectionContactEmail.trim();
    const invalidEmail = email !== "" && !isValidEmail(email);
    const invalidCollectionEmail =
      collectionEmail !== "" && !isValidEmail(collectionEmail);
    setEmailError(invalidEmail ? "Ingrese un correo electrónico válido" : null);
    setCollectionEmailError(
      invalidCollectionEmail ? "Ingrese un correo electrónico válido" : null
    );
    if (invalidEmail || invalidCollectionEmail) return;

    setIsSubmitting(true);

    try {
      // Create update object based on role
      const updateData: ClientUpdateData = {
        name: formData.name,
        email: formData.email,
        phone_number: formData.phone,
        contact_person: formData.contactPerson,
        activity: formData.activity,
        internal_notes: formData.internalNotes,
        collection_contact_person: formData.collectionContactPerson || null,
        collection_contact_email: formData.collectionContactEmail || null,
        collection_contact_phone: formData.collectionContactPhone || null,
      };

      // Only add superadmin-restricted fields if user is superadmin
      if (isSuperAdmin) {
        updateData.sales_condition_id = formData.salesConditionId || null;
        updateData.sales_channel = formData.salesChannel || null;
        updateData.auxiliary_type = formData.auxiliaryType || null;
        updateData.zone = formData.zone || null;

        // Set financial fields to 0 if prepaid condition or special condition
        if (isPrepaidCondition() || isSpecialZeroCondition()) {
          updateData.insured_amount = 0;
          updateData.trailing_payment_days = 0;
        } else {
          updateData.insured_amount = formData.insuredAmount;
          updateData.trailing_payment_days = formData.trailingPaymentDays;
        }

        // Note: The database trigger will ensure trailing_payment_days matches the sales condition
        // even if the frontend value doesn't perfectly match
      }

      // Add risk_checked if user can manage risk
      if (canManageRisk) {
        updateData.risk_checked = formData.riskChecked;
      }

      // Add siniestro and cobranza_judicial if user is admin
      if (isAdmin) {
        updateData.siniestro = formData.siniestro;
        updateData.cobranza_judicial = formData.cobranzaJudicial;
      }

      const { error } = await supabase
        .from("clients")
        .update(updateData)
        .eq("id", String(client.id));

      if (error) throw error;

      // Sync fiscal fields with BSale whenever any of them changed. BSale freezes
      // the client's razón social onto each emitted DTE at emission time, so a
      // name change that isn't pushed here would make the NEXT factura print the
      // stale name. Previously this only fired on an activity change, which let a
      // razón-social-only edit silently drift from BSale.
      const bsaleFieldsChanged =
        formData.name !== (client.name || "") ||
        formData.activity !== (client.activity || "") ||
        formData.email !== (client.email || "") ||
        formData.phone !== (client.phone || "");

      if (bsaleFieldsChanged && (client.tax_id || client.taxId)) {
        try {
          console.log("Attempting to sync fiscal data with BSale...");
          const rutToUse = client.tax_id || client.taxId;
          const { bsaleClientId, error: findError } =
            await findBSaleClientByRUT(rutToUse!);

          if (findError) {
            console.warn("Could not find BSale client:", findError);
          } else if (bsaleClientId) {
            const { success, error: syncError } = await syncClientToBSale(
              bsaleClientId,
              {
                activity: formData.activity,
                company: formData.name,
                email: formData.email,
                phone: formData.phone,
              }
            );

            if (success) {
              console.log("Fiscal data successfully synced with BSale");
            } else {
              console.warn("Failed to sync fiscal data with BSale:", syncError);
              // Don't show error to user as local update was successful
            }
          }
        } catch (syncError) {
          console.warn("Error during BSale sync:", syncError);
          // Don't throw - local update was successful
        }
      }

      toast({
        title: "Cliente actualizado",
        description:
          "La información del cliente ha sido actualizada exitosamente.",
      });

      // Call the provided callback
      onClientUpdated();

      // Dispatch a custom event to notify about client update
      window.dispatchEvent(
        new CustomEvent("client-updated", {
          detail: { clientId: client.id },
        })
      );

      onOpenChange(false);
    } catch (error: unknown) {
      console.error("Error updating client:", error);
      toast({
        title: "Error",
        description: "No se pudo actualizar la información del cliente.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Flag to determine if financial fields should be disabled
  const disableFinancialFields =
    isPrepaidCondition() || isSpecialZeroCondition();

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="z-50 max-h-[90vh] overflow-y-auto rounded-2xl sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Editar Cliente</DialogTitle>
          <DialogDescription>
            Actualice la información del cliente.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Información Cliente Section */}
          <div className="space-y-4">
            <h3 className="text-base font-semibold text-gray-800">
              Información Cliente
            </h3>

            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nombre de la Empresa</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={(e) => {
                    const { name, value } = e.target;
                    setFormData({
                      ...formData,
                      [name]: name === "name" ? value.toUpperCase() : value,
                    });
                  }}
                  placeholder="Nombre de la Empresa"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="activity">Actividad Comercial (Giro)</Label>
                <Input
                  id="activity"
                  name="activity"
                  value={formData.activity}
                  onChange={handleChange}
                  placeholder="Ej: Comercio al por mayor de productos alimenticios"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Teléfono</Label>
                <Input
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Número de Teléfono"
                />
              </div>
            </div>

            {/* Tipo de Auxiliar - Superadmin only */}
            {isSuperAdmin && (
              <div className="grid grid-cols-1 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="auxiliaryType">Tipo de Auxiliar</Label>
                  <select
                    id="auxiliaryType"
                    name="auxiliaryType"
                    value={formData.auxiliaryType}
                    onChange={handleSelectChange}
                    disabled={isLoadingAuxiliaryTypes}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <option value="">Seleccionar tipo</option>
                    {auxiliaryTypes.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>

          {/* Datos Comerciales Section */}
          <div className="space-y-4">
            <h3 className="text-base font-semibold text-gray-800">
              Datos Comerciales
            </h3>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="contactPerson">Contacto Comercial</Label>
                <Input
                  id="contactPerson"
                  name="contactPerson"
                  value={formData.contactPerson}
                  onChange={handleChange}
                  placeholder="Nombre de la persona de contacto"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Teléfono Contacto Comercial</Label>
                <Input
                  value={formData.phone}
                  className="bg-gray-50"
                  disabled
                  placeholder="Se usa el teléfono principal"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">Mail Contacto Comercial</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="contacto@empresa.com"
                  aria-invalid={emailError ? true : undefined}
                />
                {emailError && (
                  <p className="text-sm text-red-600">{emailError}</p>
                )}
              </div>

              {/* Canal de Venta - Superadmin only */}
              {isSuperAdmin ? (
                <div className="space-y-2">
                  <Label htmlFor="salesChannel">Canal de Venta</Label>
                  <select
                    id="salesChannel"
                    name="salesChannel"
                    value={formData.salesChannel}
                    onChange={handleSelectChange}
                    disabled={isLoadingSalesChannels}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <option value="">Seleccionar canal</option>
                    {salesChannels.map((channel) => (
                      <option key={channel.id} value={channel.id}>
                        {channel.name}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>Canal de Venta</Label>
                  <div className="rounded border bg-gray-50 p-3 text-sm text-gray-500">
                    Solo configurable por superadministradores
                  </div>
                </div>
              )}
            </div>

            {/* Zona - Superadmin only */}
            {isSuperAdmin && (
              <div className="grid grid-cols-1 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="zone">Zona</Label>
                  <select
                    id="zone"
                    name="zone"
                    value={formData.zone}
                    onChange={handleSelectChange}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <option value="">Seleccionar zona</option>
                    <option value="Centro">Centro</option>
                    <option value="Norte">Norte</option>
                    <option value="Sur">Sur</option>
                  </select>
                </div>
              </div>
            )}
          </div>

          {/* Datos Cobranza Section */}
          <div className="space-y-4">
            <h3 className="text-base font-semibold text-gray-800">
              Datos Cobranza
            </h3>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="collectionContactPerson">
                  Contacto Cobranza
                </Label>
                <Input
                  id="collectionContactPerson"
                  name="collectionContactPerson"
                  value={formData.collectionContactPerson}
                  onChange={(e) => {
                    const { name, value } = e.target;
                    setFormData({
                      ...formData,
                      [name]: value.toUpperCase(),
                    });
                  }}
                  placeholder="Nombre del contacto de cobranza"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="collectionContactPhone">
                  Teléfono Persona Cobranza
                </Label>
                <Input
                  id="collectionContactPhone"
                  name="collectionContactPhone"
                  value={formData.collectionContactPhone}
                  onChange={handleChange}
                  placeholder="Teléfono de contacto de cobranza"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="collectionContactEmail">
                  Mail Contacto Cobranza
                </Label>
                <Input
                  id="collectionContactEmail"
                  name="collectionContactEmail"
                  type="email"
                  value={formData.collectionContactEmail}
                  onChange={handleChange}
                  placeholder="cobranza@empresa.com"
                  aria-invalid={collectionEmailError ? true : undefined}
                />
                {collectionEmailError && (
                  <p className="text-sm text-red-600">{collectionEmailError}</p>
                )}
              </div>

              {/* Condición de Venta - Superadmin only */}
              {isSuperAdmin ? (
                <div className="space-y-2">
                  <Label htmlFor="salesCondition">Condición de Venta</Label>
                  <SalesConditionSelector
                    value={formData.salesConditionId}
                    onChange={handleSalesConditionChange}
                    placeholder="Seleccionar condición de venta"
                    disabled={isSubmitting || isLoadingSalesConditions}
                    label=""
                  />
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>Condición de Venta</Label>
                  <div className="rounded border bg-gray-50 p-3 text-sm text-gray-500">
                    Solo configurable por superadministradores
                  </div>
                </div>
              )}
            </div>

            {/* Superadmin-only financial fields in Datos Cobranza section */}
            {isSuperAdmin && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="insuredAmount">Monto Asegurado ($)</Label>
                  <Input
                    id="insuredAmount"
                    name="insuredAmount"
                    type="number"
                    value={formData.insuredAmount}
                    onChange={handleNumberChange}
                    placeholder="Monto máximo de crédito"
                    disabled={isSubmitting || disableFinancialFields}
                    className={disableFinancialFields ? "bg-gray-100" : ""}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="trailingPaymentDays">Días de Pago</Label>
                  <Input
                    id="trailingPaymentDays"
                    name="trailingPaymentDays"
                    type="number"
                    value={formData.trailingPaymentDays}
                    onChange={handleNumberChange}
                    placeholder="Días para pago"
                    disabled={isSubmitting || disableFinancialFields}
                    className={disableFinancialFields ? "bg-gray-100" : ""}
                  />
                </div>
              </div>
            )}
          </div>

          {disableFinancialFields && (
            <div className="rounded-lg bg-amber-50 p-3 text-xs text-amber-600">
              {isSpecialZeroCondition()
                ? "Para esta condición de venta, los valores de Monto Asegurado y Días de Pago se establecen automáticamente en 0."
                : "Para condiciones de pago anticipado, los valores de Monto Asegurado y Días de Pago se establecen automáticamente en 0."}
            </div>
          )}

          {/* Risk verified checkbox - visible to admin and risk roles */}
          {canManageRisk && (
            <div className="flex items-center space-x-2">
              <Checkbox
                id="riskChecked"
                checked={formData.riskChecked}
                onCheckedChange={handleRiskCheckedChange}
              />
              <Label htmlFor="riskChecked" className="cursor-pointer">
                Cliente verificado por riesgo
              </Label>
            </div>
          )}

          {/* Siniestro and Cobranza Judicial - visible only to admin/superadmin */}
          {isAdmin && (
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="siniestro"
                  checked={formData.siniestro}
                  onCheckedChange={handleSiniestroChange}
                />
                <Label htmlFor="siniestro" className="cursor-pointer">
                  Siniestro
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="cobranzaJudicial"
                  checked={formData.cobranzaJudicial}
                  onCheckedChange={handleCobranzaJudicialChange}
                />
                <Label htmlFor="cobranzaJudicial" className="cursor-pointer">
                  Cobranza Judicial
                </Label>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="internalNotes">Notas Internas</Label>
            <Textarea
              id="internalNotes"
              name="internalNotes"
              value={formData.internalNotes}
              onChange={handleTextareaChange}
              placeholder="Escriba cualquier nota interna relacionada con el cliente"
              className="h-20"
            />
          </div>

          <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-between">
            {/* Delete button - only visible to admins for non-deleted clients */}
            {isAdmin && !client.deleted_at ? (
              <Button
                type="button"
                variant="destructive"
                onClick={handleDeleteClient}
                disabled={isSubmitting}
                className="w-full sm:w-auto"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Eliminar Cliente
              </Button>
            ) : (
              <div />
            )}
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Guardando..." : "Guardar Cambios"}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditClientModal;
